import React, { useEffect } from "react";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
} from "@material-tailwind/react";
import { FormProvider, useForm } from "react-hook-form";
import { fetcherFull } from "utils/api";
import RHFInput from "../form/rhf-input";
import { toast } from "react-toastify";

export default function ProcessReportDialog({
    showProcessDialog,
    setShowProcessDialog,
    processingReport,
    surveyAbuseMutate,
    surveyRespondentAnswersMutate,
}) {
    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            admin_comment: processingReport.admin_comment || "",
            // The backend status enum is lowercase (reported|accepted|
            // rejected); reportSubmit PUTs the same strings back.
            accept_report: processingReport.status === "accepted",
            reject_report: processingReport.status === "rejected",
        },
    });
    const values = methods.watch();
    const { reset } = methods;

    // Reset once per open transition, re-reading the current report.
    // processingReport is parent state (stable identity), so it belongs in
    // the deps: switching reports resets the form even mid-open.
    useEffect(() => {
        if (showProcessDialog && processingReport) {
            reset({
                admin_comment: processingReport.admin_comment || "",
                accept_report: processingReport.status === "accepted",
                reject_report: processingReport.status === "rejected",
            });
        }
    }, [showProcessDialog, processingReport, reset]);

    const handleClose = () => {
        setShowProcessDialog((prev) => !prev);
    };
    const reportSubmit = async (values) => {
        const reportToSend = { ...processingReport };
        reportToSend.admin_comment = values.admin_comment || "";
        reportToSend.status = values.accept_report
            ? "accepted"
            : values.reject_report
                ? "rejected"
                : reportToSend.status;

        const response = await fetcherFull(
            `/v2/response/question/${reportToSend.question_nanoid}/answer/${reportToSend.respondent_answer_nanoid}/abuse/${reportToSend.id}`,
            "PUT",
            reportToSend,
        );

        if (response?.id) {
            toast.success("Report Processed");
            surveyAbuseMutate();
            surveyRespondentAnswersMutate();
        } else {
            toast.error("Error Processing Report");
        }

        setShowProcessDialog((prev) => !prev);
    };

    return (
        <Dialog
            open={showProcessDialog}
            handler={handleClose}
            className="dialog"
        >
            <DialogHeader className="dialog__header">
                Moderate Report
            </DialogHeader>

            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(reportSubmit)}>
                    <DialogBody className="dialog__body">
                        <Typography
                            variant="paragraph"
                            className="text-primary"
                        >
                            Accepting this Reported Response will hide it
                            from the survey ‘matched’ responses but it is
                            not permanently removed from the database.
                        </Typography>

                        <Typography as="span" className="dialog__section">
                            <RHFInput
                                className="font-body"
                                type="text"
                                variant="static"
                                size="md"
                                name="admin_comment"
                                placeholder="Enter an admin comment"
                            />
                        </Typography>

                        <Typography className="dialog__section flex justify-center gap-4 flex-wrap">
                            <label className="inline-flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="report_status"
                                    checked={values.accept_report}
                                    onChange={() => {
                                        methods.setValue(
                                            "accept_report",
                                            true,
                                            { shouldValidate: true },
                                        );
                                        methods.setValue(
                                            "reject_report",
                                            false,
                                            { shouldValidate: true },
                                        );
                                    }}
                                    className="form-radio accent-black"
                                />
                                <span className="ml-2 font-body text-black">
                                    Accept Report
                                </span>
                            </label>

                            <label className="inline-flex items-center cursor-pointer ml-4">
                                <input
                                    type="radio"
                                    name="report_status"
                                    checked={values.reject_report}
                                    onChange={() => {
                                        methods.setValue(
                                            "accept_report",
                                            false,
                                            { shouldValidate: true },
                                        );
                                        methods.setValue(
                                            "reject_report",
                                            true,
                                            { shouldValidate: true },
                                        );
                                    }}
                                    className="form-radio accent-black"
                                />
                                <span className="ml-2 font-body text-black">
                                    Reject Report
                                </span>
                            </label>
                        </Typography>
                    </DialogBody>

                    <DialogFooter className="dialog__footer">
                        <Button type="submit" className="btn btn--primary">
                            Save
                        </Button>
                        <Button
                            type="button"
                            className="btn btn--secondary ml-2"
                            onClick={handleClose}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </form>
            </FormProvider>
        </Dialog>
    );
}
